import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  createFaceLock, faceLockCostLabel, faceLockErrorText, FaceLockUploadError, FACELOCK_NAME_MAX, mergePhotoPicks,
  newIdempotencyKey, photoCountOk, uploadFaceLockPhotos, type FaceLockAvatar, type FaceLockEligibility,
} from "@/lib/facelock";
import { FaceLockGuide, PhotoTray } from "./FaceLockParts";
import { scanCreateExtras } from "@/lib/face-scan/caption";
import { clearSavedScan } from "@/lib/face-scan/store";

// New avatar: a name and ONE complete set of photos (owner decision
// 2026-09-30): 16 to 24 (eligibility.photo_min / photo_max). One price,
// whatever the count: next_cost (free for the first avatar, 50 after). No
// steps, no per-8 price, no strengthen afterwards.
//
// THE SCAN SLOT. The FaceLock Scan (a forced-coverage 24-frame capture, its own
// components) plugs in through `scanSlot` and hands it FaceLockScanSlotProps.
// When it has a full set it calls onCaptured(files) (24 frames); those become
// the set this dialog uploads and sends, exactly like picked photos. When the
// server takes labelled pictures (eligibility.photo_extra_max > 0) a scan may
// carry that many more past the set, and each of its photos goes with its
// label; otherwise the request is the plain list of links it always was.
// SCAN FIRST (owner, 2026-09-30): with a slot the dialog is only a short
// title, the name, and the slot's one big "Start FaceLock Scan" button (then
// the price and Create). No photo guidance and no upload tray here: those
// live in the scan's own no-camera fallback (its photo checklist). Without a
// slot the guidance and the tray are the only way in.

export type FaceLockScanSlotProps = {
  /** The fewest and the most frames/photos the server takes for one set. */
  minPhotos: number;
  maxPhotos: number;
  /** How many pictures past the set the server takes (labelled); 0: none, a scan holding more is trimmed to its set. */
  extraMax?: number;
  /** An upload or the create request is running: the slot should not capture. */
  disabled: boolean;
  /** A complete set: it replaces whatever the tray held. */
  onCaptured: (files: File[]) => void;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eligibility: FaceLockEligibility;
  onCreated: (avatar: FaceLockAvatar | null) => void;
  /** The FaceLock Scan, when the host has it (see THE SCAN SLOT above). */
  scanSlot?: (props: FaceLockScanSlotProps) => React.ReactNode;
};

export default function NewFaceLockDialog({ open, onOpenChange, eligibility, onCreated, scanSlot }: Props) {
  const { t } = useTranslation();
  const min = eligibility.photo_min;
  const max = eligibility.photo_max;
  const extraMax = Math.max(0, eligibility.photo_extra_max ?? 0);
  // A scan's pictures may go past the set by the server's extras; picked photos never do.
  const scanMax = max + extraMax;
  const [name, setName] = React.useState("");
  const [files, setFiles] = React.useState<File[]>([]);
  const [pickNote, setPickNote] = React.useState("");
  const [error, setError] = React.useState<{ text: string; buy: boolean } | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  // Photos already uploaded (a retry reuses them) and the idempotency key of
  // the request those exact inputs make.
  const uploaded = React.useRef(new Map<File, string>());
  const idem = React.useRef({ sig: "", key: "" });

  const count = files.length;
  const nameOk = name.trim().length >= 1 && name.trim().length <= FACELOCK_NAME_MAX;
  const ceiling = scanSlot ? scanMax : max;
  const setOk = photoCountOk({ photo_min: min, photo_max: ceiling }, count);
  // "24 of 24 photos" as always; a scan that carries extras reads "25 of 25", never "of 45".
  const shownMax = Math.max(max, Math.min(count, ceiling));
  const ready = nameOk && setOk && !busy;
  const countNote = setOk ? t("faceLock.create.ready", { count })
    : count === 0 ? t("faceLock.create.needPhotos", { min, max })
    : t("faceLock.create.needMore", { count: min - count, target: min });
  const cost = faceLockCostLabel(t, eligibility.next_cost);
  const blocker = !nameOk ? t("faceLock.create.needName")
    : !setOk ? (scanSlot && count === 0 ? t("faceLock.create.needScan") : t("faceLock.create.needPhotos", { min, max }))
    : "";

  const reset = () => {
    setName(""); setFiles([]); setPickNote(""); setError(null);
    uploaded.current = new Map();
    idem.current = { sig: "", key: "" };
  };

  const addPhotos = (list: FileList | null) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    const { next, unsupported, dropped } = mergePhotoPicks(files, picked, max);
    setFiles(next);
    setError(null);
    setPickNote(unsupported.length ? t("faceLock.create.unsupported", { name: unsupported[0].name })
      : dropped ? t("faceLock.create.maxReached", { max }) : "");
  };

  const captured = (frames: File[]) => {
    setFiles(frames.slice(0, scanMax));
    setPickNote("");
    setError(null);
  };

  const submit = async () => {
    if (!ready) return;
    setError(null);
    const trimmed = name.trim();
    const sig = `${trimmed}|${files.map((f) => `${f.name}_${f.size}_${f.lastModified}`).join(",")}`;
    if (idem.current.sig !== sig) idem.current = { sig, key: newIdempotencyKey() };
    try {
      const photos = await uploadFaceLockPhotos(files, uploaded.current, (n, total) => setBusy(t("faceLock.create.uploading", { n, total })));
      setBusy(t("faceLock.create.starting"));
      // FaceLock Scan frames carry their training captions (aligned with photos) and source "scan"; their labels go
      // with their links only when the server takes them (otherwise the plain list, byte for byte as before).
      const { labels, ...scan } = scanCreateExtras(files);
      const sent = extraMax > 0 && labels ? photos.map((url, i) => ({ url, ...labels[i] })) : photos;
      const res = await createFaceLock({ name: trimmed, photos: sent, idempotency_key: idem.current.key, ...scan });
      if (res.ok === false) {
        setError({ text: faceLockErrorText(t, res), buy: res.status === 402 || res.error === "insufficient_credits" });
        // A refused request is final; trying again is a new request.
        if (res.status >= 400 && res.status < 500) idem.current = { sig: "", key: "" };
        return;
      }
      if (scan.source) void clearSavedScan();
      reset();
      onCreated(res.data);
      onOpenChange(false);
    } catch (e: unknown) {
      setError({
        text: e instanceof FaceLockUploadError
          ? t("faceLock.create.uploadFailed", { name: e.fileName, reason: e.reason })
          : t("faceLock.errors.network"),
        buy: false,
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent overlayClassName="z-[400]" className="z-[400] max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="facelock-create-dialog">
        <DialogHeader>
          <DialogTitle>{t("faceLock.create.title")}</DialogTitle>
          {/* Scan first: the dialog stays short (the description is for screen readers only). */}
          <DialogDescription className={scanSlot ? "sr-only" : undefined}>{t("faceLock.subtitle")}</DialogDescription>
        </DialogHeader>

        {!scanSlot && <FaceLockGuide />}

        <div className="grid gap-1.5">
          <label htmlFor="facelock-name" className="text-sm font-semibold">{t("faceLock.create.nameLabel")}</label>
          <Input
            id="facelock-name"
            data-testid="facelock-create-name"
            value={name}
            maxLength={FACELOCK_NAME_MAX}
            placeholder={t("faceLock.create.namePlaceholder")}
            disabled={!!busy}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="grid gap-2">
          {!scanSlot && <div className="text-sm font-semibold">{t("faceLock.create.photosLabel")}</div>}
          {scanSlot && (
            <div data-testid="facelock-create-scan">
              {scanSlot({ minPhotos: min, maxPhotos: max, extraMax, disabled: !!busy, onCaptured: captured })}
            </div>
          )}
          {/* With the scan plugged in, photos come only through it: its own
              no-camera fallback checks them against the same checklist
              (owner rule 2026-09-30: no upload button next to the scanner). */}
          {!scanSlot && (
            <PhotoTray
              files={files}
              onAdd={addPhotos}
              onRemove={(i) => { setFiles((prev) => prev.filter((_, j) => j !== i)); setPickNote(""); }}
              disabled={!!busy}
              full={count >= max}
              testIdPrefix="facelock-create-photos"
              uploadLabel={t("faceLock.create.upload")}
              addMoreLabel={t("faceLock.create.addMore")}
            />
          )}
          {/* The photo count is photo-upload talk: with the scan it shows only once the scan handed a set over. */}
          {(!scanSlot || count > 0) && (
            <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span data-testid="facelock-create-count" className="font-semibold">{t("faceLock.create.count", { count, max: shownMax })}</span>
              <span data-testid="facelock-create-count-note" className={setOk ? "text-primary" : "text-muted-foreground"}>{countNote}</span>
            </div>
          )}
          {pickNote && <div data-testid="facelock-create-pick-note" className="text-sm text-amber-500">{pickNote}</div>}
        </div>

        <p data-testid="facelock-create-price" className="text-sm text-muted-foreground">
          {eligibility.next_cost > 0 ? t("faceLock.create.pricePaid", { cost }) : t("faceLock.create.priceFree")}
        </p>

        {error && (
          <div data-testid="facelock-create-error" role="alert" className="text-sm text-red-500">
            {error.text}{" "}
            {error.buy && <a href="/pricing" className="font-semibold underline">{t("faceLock.gate.buyCredits")}</a>}
          </div>
        )}

        <div className="grid gap-1.5">
          <Button type="button" data-testid="facelock-create-submit" disabled={!ready} onClick={() => void submit()}>
            {t("faceLock.create.submit", { cost })}
          </Button>
          <div data-testid="facelock-create-status" className="text-sm text-muted-foreground" aria-live="polite">{busy || blocker}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
